import type {
  AccountMenu,
  ButtonWidget,
  BuyflowHeader,
  BuyflowStep,
  IconLink,
} from '@/lib/TypesOpenApi';
import type {TextLink} from '@/lib/types';
import {persist} from '@/lib/vatio-persistence';
import {useSnapshot} from 'valtio';
import {logout} from './UserProfileState';
import {isCiamEnabled} from '@gfiber/pixel-party';

export interface ActionLink extends Omit<TextLink, 'url'> {
  onClick: () => void;
}

export interface ActionableMenuSections {
  links: Array<TextLink | ActionLink>;
}

export interface HeaderAcccountMenu extends Omit<AccountMenu, 'menuSections'> {
  menuSections: Array<ActionableMenuSections>;
  signOut?: SignOutWidget;
}

export interface SignOutWidget extends ButtonWidget {
  onClick: () => void;
}

/**
 * Header from back-end missing many parts,
 * that can be added it future (or not).
 * As a result there the done mapping that partly enriches data with mocks.
 * So in future it will be easier to switch it.
 * */
export interface HeaderWidget {
  productWidget?: BuyflowHeader;
  logo: IconLink;
  compactLogo: IconLink;
  phoneIcon: IconLink;
  phoneNumber: TextLink;
  chatIcon: IconLink;
  isChatOpen: boolean;
  menu?: HeaderAcccountMenu;
}

export const openChatIframe = () => {
  headerWidget.isChatOpen = true;
};

export const closeChatIframe = () => {
  headerWidget.isChatOpen = false;
};

//export const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
// Header comes from the return of the START_BUYFLOW request.
// if add any new field, add the new field to the below reset function also
export const headerWidget = persist<HeaderWidget>('header', {
  productWidget: undefined,
  isChatOpen: false,
  get logo() {
    const logo = this.productWidget?.gfiberIcon;
    return {
      icon: logo?.icon,
      // response value empty in reality
      url: logo?.url || '/',
    };
  },
  get compactLogo() {
    const logo = this.productWidget?.gfiberCompactIcon;
    return {
      icon: logo?.icon,
      // response value empty in reality
      url: logo?.url || this.logo.url,
    };
  },

  get phoneNumber() {
    const link = this.productWidget?.number;
    return {
      text: link?.text!,
      // response value empty in reality
      url: link?.url || (link?.text ? `tel:${trimNumber(link.text)}` : ''),
    };
  },
  get phoneIcon() {
    // real phoneIcon from API is deprecated and empty
    return {
      icon: 'CALL',
      url: this.phoneNumber.url,
    };
  },
  get chatIcon() {
    return {
      icon: 'CHAT',
      url: 'https://fiber.google.com/chat',
    };
  },
  get menu() {
    return {
      // deprecated and empty in response, but replacement not exists
      menuSections: [
        {
          links: [
            !isCiamEnabled() && {
              text: 'Manage Account',
              url: 'https://myaccount.google.com/',
            },
            // production behaivor of old application
            // logout user, so he will be need to login back to switch account
            {text: 'Switch Account', onClick: () => logout(true)},
          ].filter(Boolean),
        },
        !isCiamEnabled() && {
          links: [
            {text: 'Privacy', url: 'https://fiber.google.com/legal/privacy/'},
          ],
        },
      ].filter(Boolean),
      signOut: {
        ...(this.productWidget?.accountMenu?.signOut || {}),
        onClick: () => logout(false),
      },
    } as HeaderAcccountMenu;
  },
});

export const resetHeaderWidget = () => {
  headerWidget.productWidget = undefined;
};

export const useHeaderWidget = () => useSnapshot(headerWidget);

/**
 * Remove majority of redability symbols so value can be used in url
 * @example "(844) 363-4237" -> 8443634237
 * */
export function trimNumber(text: string): string {
  return text.replace(/[^\d]/g, '');
}

export function setHeader(step: BuyflowStep) {
  let widget;
  const rootObj = step.buyflowWidgets[0];
  if (rootObj) {
    for (const key in rootObj) {
      // There is no common type for steps with buyflowHeader.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const obj = rootObj as any;
      widget = obj[key];
      const buyflowHeader = widget?.buyflowHeader as BuyflowHeader;
      if (buyflowHeader) {
        headerWidget.productWidget = buyflowHeader;
      }
      break;
    }
  }
}
